import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Desguace en Barberà del Vallès – Compra de Coches',
  description: 'Compramos coches en Barberà del Vallès al mejor precio. Siniestrados, averiados, sin ITV. Recogida gratuita y pago inmediato.',
  provider: {
    '@type': 'AutoDealer',
    name: 'Desguace Barcelona',
    telephone: '+34932932913',
    address: { '@type': 'PostalAddress', addressLocality: 'Barberà del Vallès', addressRegion: 'Cataluña', addressCountry: 'ES' },
  },
  areaServed: { '@type': 'City', name: 'Barberà del Vallès' },
  url: `${siteUrl}/desguace-barbera`,
};

export default function DesguaceBarberaPage() {
  return (
    <Suspense fallback={<div>Cargando...</div>}>
      <div className="min-h-screen bg-white">
        <SeoHead
          title="Desguace en Barberà del Vallès | Mejor Precio | Recogida Gratis"
          description="Desguace en Barberà del Vallès. Compramos coches siniestrados, averiados o sin ITV al mejor precio. Recogida gratuita en Barberà y Vallès Occidental. Pago inmediato."
          keywords="desguace Barberà del Vallès, vender coche Barberà, compra coches Barberà Vallès, desguace Vallès Occidental"
          canonical={`${siteUrl}/desguace-barbera`}
          jsonLd={jsonLd}
        />
        <Navbar />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-br from-[#1a2332] via-[#2d3748] to-[#1a2332] py-24">
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Desguace en Barberà del Vallès
              </h1>
              <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
                Compramos tu coche en Barberà del Vallès al mejor precio. Tasación gratuita, recogida inmediata y pago al instante. Servicio profesional en toda la ciudad.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20tasar%20mi%20coche%20en%20Barberà%20del%20Vallès" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#1da851] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-whatsapp-line mr-2 text-2xl"></i>
                  Tasación Gratis en Barberà
                </a>
                <a 
                  href="tel:+34932932913" 
                  className="bg-white text-[#1a2332] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-phone-line mr-2 text-2xl"></i>
                  932 932 913
                </a>
              </div>
            </div>
          </section>

          {/* Zonas de Barberà */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-[#1a2332] mb-4">
                  Servicio en Todas las Zonas de Barberà del Vallès
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Compramos coches en todos los barrios y zonas de Barberà del Vallès con recogida gratuita
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  'Centre',
                  'Can Llobet',
                  'Baricentro',
                  'Ponent',
                  'Can Gordi',
                  'Santiga',
                  'Can Carreras',
                  'Plana del Vallès',
                  'Can Casacuberta',
                  'Canaletes',
                  'Torre Romeu',
                  'Polígono Industrial'
                ].map((zona) => (
                  <div 
                    key={zona}
                    className="bg-[#fafbfc] p-4 rounded-lg border border-gray-200 hover:border-[#ff6b35] transition-all hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#ff6b35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-[#ff6b35] text-lg"></i>
                      </div>
                      <span className="text-[#1a2332] font-medium text-sm">{zona}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Ventajas */}
          <section className="py-20 bg-[#fafbfc]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-[#1a2332] mb-4">
                  ¿Por Qué Elegirnos en Barberà del Vallès?
                </h2>
                <p className="text-lg text-gray-600">
                  Somos el desguace de confianza en Barberà del Vallès
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-money-euro-circle-line text-[#ff6b35] text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Mejor Precio Garantizado</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Ofrecemos las mejores tasaciones del mercado en Barberà del Vallès. Pago inmediato en efectivo o transferencia.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-truck-line text-[#ff6b35] text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Recogida Gratuita</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Recogemos tu vehículo gratis en cualquier zona de Barberà del Vallès. Sin costes ocultos ni sorpresas.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-file-text-line text-[#ff6b35] text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Gestión Completa</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Nos encargamos de toda la documentación y trámites. Baja de tráfico incluida sin coste adicional.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Tipos de Coches */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-[#1a2332] mb-4">
                  Compramos Todo Tipo de Coches en Barberà del Vallès
                </h2>
                <p className="text-lg text-gray-600">
                  No importa el estado de tu vehículo, te hacemos una oferta
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-car-line', title: 'Coches Siniestrados', desc: 'Compramos vehículos con siniestro total' },
                  { icon: 'ri-tools-line', title: 'Coches Averiados', desc: 'Con averías mecánicas o eléctricas' },
                  { icon: 'ri-file-warning-line', title: 'Sin ITV', desc: 'Vehículos sin pasar la inspección' },
                  { icon: 'ri-alert-line', title: 'Accidentados', desc: 'Coches con daños por accidente' }
                ].map((tipo) => (
                  <div 
                    key={tipo.title}
                    className="bg-[#fafbfc] p-6 rounded-xl border border-gray-200 hover:border-[#ff6b35] transition-all hover:shadow-md"
                  >
                    <div className="w-14 h-14 bg-[#ff6b35]/10 rounded-full flex items-center justify-center mb-4">
                      <i className={`${tipo.icon} text-[#ff6b35] text-2xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#1a2332] mb-2">{tipo.title}</h3>
                    <p className="text-gray-600 text-sm">{tipo.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Final */}
          <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8555]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                ¿Listo para Vender tu Coche en Barberà del Vallès?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Contáctanos ahora y recibe tu tasación gratuita en minutos
              </p>
              <a 
                href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20tasar%20mi%20coche%20en%20Barberà%20del%20Vallès" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-[#ff6b35] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2 text-2xl"></i>
                Contactar por WhatsApp
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </Suspense>
  );
}
